import React, { useState } from 'react';
import { useNotification } from 'Contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import secureStorage from 'hooks/secureStorage';

const NotificationBell = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotification();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleMarkAllRead = async (e) => {
        e.stopPropagation();
        await markAllAsRead();
    };

    // 1=Vert, 2=Orange, 3=Rouge selon specs
    const getBackgroundColor = (level) => {
        switch (level) {
            case 1: return 'bg-yellow-50 border-yellow-200';
            case 2: return 'bg-orange-100 border-orange-500';
            case 3: return 'bg-red-100 border-red-500';
            default: return 'bg-gray-100 border-gray-300';
        }
    };

    const getTextColor = (level) => {
        switch (level) {
            case 1: return 'text-yellow-800';
            case 2: return 'text-orange-800';
            case 3: return 'text-red-800';
            default: return 'text-gray-800';
        }
    };

    const handleNotificationClick = async (notif) => {
        // Marquer comme lue
        if (!notif.isRead) {
            await markAsRead(notif.id);
        }

        setIsOpen(false);

        if (notif.targetId === 'GLOBAL_DELAY') {
            navigate('/presfor/psfcpresforenqcf/dossier-non-valide');
        } else if (notif.targetId === 'CONTRAT_SOUMIS') {
            navigate('/presfor/psfcpresforenqpromocontrat');
        } else if (notif.targetId.startsWith('REGION_DELAY_')) {
            const regionCode = notif.targetId.replace('REGION_DELAY_', '');
            navigate('/presfor/psfcpresforenqcf/dossier-non-valide', {
                state: { regionFilter: regionCode }
            });
        } else {
            // C'est un dossier individuel
            secureStorage.setItem('enquete_courante', JSON.stringify({
                code_dcf: notif.targetId,
            }));
            navigate('/presfor/psfcpresforlivrablescf');
        }
    };

    return (
        <div className="relative inline-block text-left mr-4">
            <div className="relative cursor-pointer mt-2" onClick={toggleDropdown}>
                <svg
                    className=""
                    style={{ color: '#113946', cursor: 'pointer' }}
                    width={30}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                        <g clipPath="url(#clip0_15_159_notif)">
                            <rect width="24" height="24" fill="white"></rect>
                            <path
                                d="M9.5 19C8.89555 19 7.01237 19 5.61714 19C4.87375 19 4.39116 18.2177 4.72361 17.5528L5.57771 15.8446C5.85542 15.2892 6 14.6774 6 14.0564C6 13.2867 6 12.1434 6 11C6 9 7 5 12 5C17 5 18 9 18 11C18 12.1434 18 13.2867 18 14.0564C18 14.6774 18.1446 15.2892 18.4223 15.8446L19.2764 17.5528C19.6088 18.2177 19.1253 19 18.382 19H14.5M9.5 19C9.5 21 10.5 22 12 22C13.5 22 14.5 21 14.5 19M9.5 19C11.0621 19 14.5 19 14.5 19"
                                stroke="#113946"
                                strokeLinejoin="round"
                            ></path>
                            <path
                                d="M12 5V3"
                                stroke="#113946"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            ></path>
                        </g>
                        <defs>
                            <clipPath id="clip0_15_159_notif">
                                <rect width="24" height="24" fill="#113946"></rect>
                            </clipPath>
                        </defs>
                    </g>
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full" style={{ transform: 'translate(25%, -25%)' }}>
                        {unreadCount}
                    </span>
                )}
            </div>

            {isOpen && (
                <div
                    className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-[#FFFFFF] ring-1 ring-black ring-opacity-5 z-50 overflow-hidden"
                    style={{ zIndex: 9999 }}
                >
                    <div className="py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center px-4">
                        <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="bg-transparent text-[#113946] text-xs font-semibold hover:underline cursor-pointer"
                            >
                                Tout lu
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <p className="text-sm text-gray-500 p-4 text-center">Aucune notification</p>
                        ) : (
                            notifications.map((notif) => (
                                <div
                                    key={notif.id}
                                    className={`p-3 border-l-4 border-b cursor-pointer hover:bg-opacity-80 transition-colors ${notif.isRead ? 'opacity-60' : ''} ${getBackgroundColor(notif.reminderLevel)}`}
                                    onClick={() => handleNotificationClick(notif)}
                                >
                                    <p className={`text-sm font-semibold ${getTextColor(notif.reminderLevel)} mb-2 flex items-start`}>
                                        {notif.targetId === 'GLOBAL_DELAY' && <span className="mr-2 text-base leading-none"></span>}
                                        <span>{notif.message}</span>
                                    </p>
                                    <div className="text-xs text-gray-600 flex justify-between items-center">
                                        <span className="flex items-center gap-1 font-medium">
                                            <span className="text-sm"></span> {new Date(notif.createdAt).toLocaleDateString('fr-FR')} à {new Date(notif.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        <span className="cursor-pointer bg-transparent text-blue-700 hover:text-blue-900 font-bold whitespace-nowrap ml-2">
                                            {notif.targetId === 'GLOBAL_DELAY' ? '[ Voir la liste ]' :
                                                notif.targetId === 'CONTRAT_SOUMIS' ? '[ Voir les contrats ]' :
                                                    '[ Ouvrir le dossier ]'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
